import { useState } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";
import { useLanguage } from "../../../context/LanguageContext";

interface Props
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

export default function PasswordField({
  label,
  name,
  value,
  error,
  placeholder,
  onChange,
  required = true,
  className = "",
  ...props
}: Props) {
  const [show, setShow] = useState(false);
  const { t } = useLanguage();

  const inputId = props.id || name;

  return (
    <div>
      <label
        htmlFor={inputId}
        className="flex items-center text-sm font-medium text-gray-700 mb-2"
      >
        {label}

        {required && (
          <span className="text-red-500 ms-1">
            *
          </span>
        )}
      </label>

      <div className="relative">
        <span className="absolute start-4 top-1/2 -translate-y-1/2 text-gray-400">
          <Lock size={16} />
        </span>

        <input
          id={inputId}
          type={show ? "text" : "password"}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          aria-invalid={!!error}
          className={`
            w-full ps-11 pe-12 py-3
            border rounded-xl
            focus:border-[#b3d130]
            focus:outline-none
            focus:ring-2
            focus:ring-[#b3d130]/20
            transition-all
            ${error
              ? "border-red-500 bg-red-50"
              : "border-gray-300"
            }
            ${className}
          `}
          {...props}
        />

        <button
          type="button"
          onClick={() => setShow(!show)}
          aria-label={
            show
              ? t("auth.passwordField.hidePassword")
              : t("auth.passwordField.showPassword")
          }
          className="absolute end-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {show ? (
            <EyeOff size={18} />
          ) : (
            <Eye size={18} />
          )}
        </button>
      </div>

      {error && (
        <p className="text-xs text-red-500 mt-1">
          {error}
        </p>
      )}
    </div>
  );
}